/**
 * SingleRowUnitDoorEditor - Door editor for single-row template units
 *
 * Shows only available walls based on unit position:
 * - First unit (index 0): front + back + right walls (visually appears on LEFT)
 * - Interior units: front + back walls
 * - Last unit: front + back + left walls (visually appears on RIGHT)
 * - Single unit (count=1): front + back + left + right walls
 *
 * NOTE: Left/right are swapped to match user perspective when facing the
 * building from outside (front). The 3D coordinate system is inverted,
 * so we compensate here.
 */

import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Plus, Copy, DoorOpen, AlertTriangle } from 'lucide-react'
import type { DoorSpec, DoorType, UnitDoorsConfig } from '@/lib/templates/types'
import { validateWallDoors } from '@/lib/templates/single-unit-community'
import { DoorEditor } from '../shared/DoorEditor'

type AvailableWall = 'front' | 'back' | 'left' | 'right'

interface SingleRowUnitDoorEditorProps {
  /** Unit index (0-based) */
  unitIndex: number
  /** Total number of units in the row */
  unitCount: number
  /** Door configuration for this unit */
  doors: UnitDoorsConfig | undefined
  /** Callback when doors change */
  onChange: (doors: UnitDoorsConfig) => void
  /** Width of the unit (for south wall) */
  unitWidthFt: number
  /** Depth of the unit (for east/west walls) */
  unitDepthFt: number
  /** Height of the unit (max door height) */
  unitHeightFt: number
  /** Copy this unit's doors to all other units */
  onCopyToAll?: () => void
}

const WALL_LABELS: Record<AvailableWall, string> = {
  front: 'Front',
  back: 'Back',
  left: 'Left',
  right: 'Right',
}

export function SingleRowUnitDoorEditor({
  unitIndex,
  unitCount,
  doors,
  onChange,
  unitWidthFt,
  unitDepthFt,
  unitHeightFt,
  onCopyToAll,
}: SingleRowUnitDoorEditorProps) {
  const isFirstUnit = unitIndex === 0
  const isLastUnit = unitIndex === unitCount - 1

  // Determine which walls are available based on unit position
  // NOTE: left/right swapped to match visual appearance when facing building front
  const availableWalls = useMemo((): AvailableWall[] => {
    const walls: AvailableWall[] = ['front', 'back'] // Front and back always available

    // First unit (visually on left side) gets right wall option
    if (isFirstUnit) {
      walls.push('right')
    }
    // Last unit (visually on right side) gets left wall option
    if (isLastUnit) {
      walls.push('left')
    }

    return walls
  }, [isFirstUnit, isLastUnit])

  // Ensure doors object exists
  const safeDoors: UnitDoorsConfig = doors || { front: [], back: [], left: [], right: [] }

  /** Get wall length based on wall direction */
  const getWallLength = (wall: AvailableWall): number => {
    return (wall === 'front' || wall === 'back') ? unitWidthFt : unitDepthFt
  }

  /** Validate walls and memoize results */
  const validationErrors = useMemo(() => {
    const errors: Record<AvailableWall, string[]> = {
      front: [],
      back: [],
      left: [],
      right: [],
    }

    for (const wall of availableWalls) {
      const wallDoors = safeDoors[wall] || []
      const wallLength = getWallLength(wall)
      const result = validateWallDoors(wallDoors, wallLength)
      errors[wall] = result.errors
    }

    return errors
  }, [safeDoors, unitWidthFt, unitDepthFt, availableWalls])

  /** Check if any wall has errors */
  const hasErrors = useMemo(() => {
    return availableWalls.some((wall) => validationErrors[wall].length > 0)
  }, [validationErrors, availableWalls])

  /** Add a new door to a wall */
  const addDoor = (wall: AvailableWall) => {
    const wallLength = getWallLength(wall)
    // Calculate door width first so we can compute edge-based offset
    const newDoorWidth = Math.min(36, wallLength - 4)
    const newDoor: DoorSpec = {
      // Edge-based offset: door left edge at this position, centered on wall
      offset_ft: (wallLength - newDoorWidth) / 2,
      width_ft: newDoorWidth,
      height_ft: Math.min(20, unitHeightFt - 2),
      type: 'bifold' as DoorType,
    }

    onChange({
      ...safeDoors,
      [wall]: [...(safeDoors[wall] || []), newDoor],
    })
  }

  /** Remove a door from a wall */
  const removeDoor = (wall: AvailableWall, index: number) => {
    onChange({
      ...safeDoors,
      [wall]: (safeDoors[wall] || []).filter((_, i) => i !== index),
    })
  }

  /** Update one or more door properties atomically */
  const updateDoor = (
    wall: AvailableWall,
    index: number,
    updates: Partial<DoorSpec>
  ) => {
    const updatedDoors = [...(safeDoors[wall] || [])]
    updatedDoors[index] = { ...updatedDoors[index], ...updates }
    onChange({
      ...safeDoors,
      [wall]: updatedDoors,
    })
  }

  /** Count total doors across available walls */
  const totalDoors = availableWalls.reduce(
    (sum, wall) => sum + (safeDoors[wall]?.length || 0),
    0
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
          <DoorOpen className="h-4 w-4" />
          Doors ({totalDoors})
          {hasErrors && (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          )}
        </h4>
        {onCopyToAll && unitCount > 1 && totalDoors > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onCopyToAll}
          >
            <Copy className="h-3 w-3 mr-1" />
            Apply to all units
          </Button>
        )}
      </div>

      {availableWalls.length === 2 && (
        <p className="text-xs text-muted-foreground">
          Interior units only have front and back wall access.
        </p>
      )}

      <Accordion type="multiple" className="w-full">
        {availableWalls.map((wall) => {
          const wallDoors = safeDoors[wall] || []
          const wallErrors = validationErrors[wall]
          const hasWallErrors = wallErrors.length > 0

          return (
            <AccordionItem key={wall} value={wall}>
              <AccordionTrigger className="text-sm py-2">
                <div className="flex items-center gap-2">
                  <span>{WALL_LABELS[wall]}</span>
                  <span className="text-xs text-muted-foreground">
                    ({wallDoors.length} door{wallDoors.length !== 1 ? 's' : ''})
                  </span>
                  <span className="text-xs text-muted-foreground">
                    • {getWallLength(wall).toFixed(1)} ft
                  </span>
                  {hasWallErrors && (
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {/* Validation errors */}
                  {hasWallErrors && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-2">
                      {wallErrors.map((error, i) => (
                        <p key={i} className="text-xs text-destructive flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}

                  {wallDoors.map((door, index) => (
                    <DoorEditor
                      key={index}
                      door={door}
                      index={index}
                      wallLength={getWallLength(wall)}
                      maxHeight={unitHeightFt}
                      onUpdate={(updates) => updateDoor(wall, index, updates)}
                      onRemove={() => removeDoor(wall, index)}
                    />
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addDoor(wall)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Door
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}
